import React, { useState, useEffect, useCallback } from 'react';
// Removed Redux auth selectors - now using useAuth() hook
import { useUserData } from '../../hooks/useUserData';
import {
  Box,
  Typography,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Button,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Chip,
  useTheme,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import RadiologyIcon from '@mui/icons-material/CameraAlt';
import BloodtypeIcon from '@mui/icons-material/Bloodtype';
import PsychologyIcon from '@mui/icons-material/Psychology';

// Import existing services
import { ProcedureService } from '../../services/procedureService';
import { useSelector, useDispatch } from 'react-redux';
import { TestType } from '../../types/procedures';
import { createPrescription } from '../../redux/slices/prescriptionsSlice';

// Grid-based styled components
const GridCard = styled(Card)(({ theme }) => ({
  borderRadius: '8px',
  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.1)',
  border: `1px solid ${theme.palette.divider}`,
  height: '100%',
  minHeight: '120px',
}));

const GridCategoryHeader = styled(CardHeader)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  padding: theme.spacing(0.5, 1),
  '& .MuiCardHeader-title': {
    fontSize: '0.7rem',
    fontWeight: 600,
    lineHeight: '1.1',
  },
}));

const GridOrderItem = styled(FormControlLabel)(({ theme }) => ({
  width: '100%',
  marginLeft: 0,
  marginRight: 0,
  marginBottom: 0,
  '& .MuiCheckbox-root': {
    padding: theme.spacing(0.2),
  },
  '& .MuiFormControlLabel-label': {
    fontSize: '0.65rem',
    lineHeight: '1',
    color: theme.palette.text.primary,
  },
}));

// Quick order categories with essential items
const quickOrderCategories = {
  chemistry: {
    title: 'Lab - Chemistry',
    icon: <MedicalServicesIcon />,
    color: '#2e7d32',
    items: [
      { id: 'bmp', name: 'Basic Metabolic Panel', testType: TestType.LAB },
      { id: 'cmp', name: 'Comprehensive Metabolic Panel', testType: TestType.LAB },
      { id: 'lipase', name: 'Lipase Level', testType: TestType.LAB },
      { id: 'lactic_acid', name: 'Lactic Acid', testType: TestType.LAB },
    ]
  },
  cardiac: {
    title: 'Lab - Cardiac',
    icon: <MonitorHeartIcon />,
    color: '#ed6c02',
    items: [
      { id: 'troponin_i', name: 'Troponin I', testType: TestType.LAB },
      { id: 'pro_bnp', name: 'Pro-BNP', testType: TestType.LAB },
      { id: 'ck_total', name: 'CK total and CKMB', testType: TestType.LAB },
    ]
  },
  bloodBank: {
    title: 'Blood Bank',
    icon: <BloodtypeIcon />,
    color: '#d32f2f',
    items: [
      { id: 'type_screen', name: 'Type and Screen', testType: TestType.LAB },
      { id: 'crossmatch', name: 'Crossmatch', testType: TestType.LAB },
    ]
  },
  imaging: {
    title: 'Imaging - CT',
    icon: <RadiologyIcon />,
    color: '#9c27b0',
    items: [
      { id: 'ct_head', name: 'CT Head W/O Contrast', testType: TestType.IMAGING },
      { id: 'ct_chest', name: 'CT Chest W/ Contrast', testType: TestType.IMAGING },
      { id: 'ct_abdomen', name: 'CT Abdomen/Pelvis W/ Contrast', testType: TestType.IMAGING },
      { id: 'cta_chest', name: 'CTA Chest', testType: TestType.IMAGING },
    ]
  },
  medications: {
    title: 'Medications',
    icon: <LocalPharmacyIcon />,
    color: '#1976d2',
    items: [
      { id: 'acetaminophen', name: 'Acetaminophen 325mg', testType: TestType.MEDICATION },
      { id: 'ibuprofen', name: 'Ibuprofen 600mg', testType: TestType.MEDICATION },
      { id: 'morphine', name: 'Morphine injection', testType: TestType.MEDICATION },
    ]
  },
  consults: {
    title: 'Consults',
    icon: <PsychologyIcon />,
    color: '#607d8b',
    items: [
      { id: 'cardiology', name: 'Cardiology Consult', testType: TestType.REFERRAL },
      { id: 'surgery', name: 'General Surgery Consult', testType: TestType.REFERRAL },
      { id: 'neurology', name: 'Neurology Consult', testType: TestType.REFERRAL },
    ]
  },
};

const QuickListIntegrated = ({ 
  patientId,
  onOrdersSelected,
  onOrdersCreated,
  readOnly = false,
  maxHeight = '300px',
  showNewOrders = false,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { user, defaultFacility } = useUserData();

  
  const [selectedOrders, setSelectedOrders] = useState({});
  const [recentOrders, setRecentOrders] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, orders: [] });

  const loadRecentOrders = useCallback(async () => {
    try {
      const orders = await ProcedureService.getProceduresByPatient(patientId, 3);
      setRecentOrders(orders);
    } catch (error) {
      console.error('Error loading recent orders:', error);
    }
  }, [patientId]);

  // Load recent orders if needed
  useEffect(() => {
    if (showNewOrders && patientId) {
      loadRecentOrders();
    }
  }, [patientId, showNewOrders, loadRecentOrders]);

  const handleOrderToggle = (categoryKey, itemId) => {
    if (readOnly) return;

    const newSelected = { ...selectedOrders };
    const categoryPath = `${categoryKey}.${itemId}`;
    
    if (newSelected[categoryPath]) {
      delete newSelected[categoryPath];
    } else {
      newSelected[categoryPath] = {
        categoryKey,
        itemId,
        category: quickOrderCategories[categoryKey],
        item: quickOrderCategories[categoryKey].items.find(item => item.id === itemId)
      };
    }
    
    setSelectedOrders(newSelected);
    
    if (onOrdersSelected) {
      onOrdersSelected(Object.values(newSelected));
    }
  };

  const handleSelectAllInCategory = (categoryKey) => {
    if (readOnly) return;

    const category = quickOrderCategories[categoryKey];
    const newSelected = { ...selectedOrders };
    
    const categoryItems = category.items;
    const selectedItemsInCategory = categoryItems.filter(item => 
      newSelected[`${categoryKey}.${item.id}`]
    );
    
    const allSelected = selectedItemsInCategory.length === categoryItems.length;
    
    if (allSelected) {
      // Deselect all items in category
      categoryItems.forEach(item => {
        delete newSelected[`${categoryKey}.${item.id}`];
      });
    } else {
      // Select all items in category
      categoryItems.forEach(item => {
        newSelected[`${categoryKey}.${item.id}`] = {
          categoryKey,
          itemId: item.id,
          category,
          item
        };
      });
    }
    
    setSelectedOrders(newSelected);
    
    if (onOrdersSelected) {
      onOrdersSelected(Object.values(newSelected));
    }
  };

  const getCategorySelectedCount = (categoryKey) => {
    const category = quickOrderCategories[categoryKey];
    return category.items.filter(item => 
      selectedOrders[`${categoryKey}.${item.id}`]
    ).length;
  };

  const handleSignOrders = () => {
    const orders = Object.values(selectedOrders);
    setConfirmDialog({ open: true, orders });
  };

  const confirmSignOrders = async () => {
    const orders = confirmDialog.orders;
    setConfirmDialog({ open: false, orders: [] });
    
    try {
      const createdOrders = [];
      
      // Group orders by type for different processing
      const medications = orders.filter(order => order.item.testType === TestType.MEDICATION);
      const procedures = orders.filter(order => [TestType.LAB, TestType.IMAGING].includes(order.item.testType));
      const referrals = orders.filter(order => order.item.testType === TestType.REFERRAL);
      
      // Process medications through prescription flow
      if (medications.length > 0) {
        const medicationData = medications.map(order => ({
          name: order.item.name,
          dosage: order.item.name.includes('mg') ? order.item.name.match(/(\d+mg)/)?.[1] : '',
          route: 'Oral', // Default route - could be enhanced
          frequency: 'As needed', // Default frequency
          duration: '', // Could be enhanced
          notes: `Quick order: ${order.item.name}`,
          status: 'Saved as Draft'
        }));

        const prescriptionResult = await dispatch(createPrescription({
          patientId,
          medications: medicationData,
          pharmacy: null // Will need to be set by user later
        })).unwrap();
        
        createdOrders.push(...medicationData.map(med => ({ ...med, type: 'Prescription' })));
      }
      
      // Process lab tests and imaging through procedure flow with "Saved as Draft" status
      for (const order of procedures) {
        const orderData = {
          patientId,
          facilityId: defaultFacility?.id, // Include the required facilityId
          orderDate: new Date(),
          testType: order.item.testType,
          testCode: order.item.id,
          description: order.item.name,
          status: 'Saved as Draft', // New status for signed but not sent orders
          orderingProvider: user?.name || 'Unknown Provider',
          priority: 'Normal',
          notes: `Quick order: ${order.item.name}`,
          signedDate: new Date(), // Track when order was signed
        };
        
        const newOrder = await ProcedureService.createProcedure(orderData);
        createdOrders.push(newOrder);
      }
      
      // Process referrals with "Saved as Draft" status
      for (const order of referrals) {
        const orderData = {
          patientId,
          facilityId: defaultFacility?.id, // Include the required facilityId
          orderDate: new Date(),
          testType: order.item.testType,
          testCode: order.item.id,
          description: order.item.name,
          status: 'Saved as Draft', // New status for signed but not sent orders
          orderingProvider: user?.name || 'Unknown Provider',
          priority: 'Normal',
          notes: `Quick referral: ${order.item.name}`,
          signedDate: new Date(), // Track when order was signed
        };
        
        const newOrder = await ProcedureService.createProcedure(orderData);
        createdOrders.push(newOrder);
      }

      // Success message with breakdown
      let message = `Successfully signed ${createdOrders.length} orders`;
      if (medications.length > 0) message += ` (${medications.length} prescriptions)`;
      if (procedures.length > 0) message += ` (${procedures.length} procedures)`;
      if (referrals.length > 0) message += ` (${referrals.length} referrals)`;
      message += '. Orders are saved as drafts and need to be reviewed in the Orders tab.';

      setSnackbar({
        open: true,
        message,
        severity: 'success'
      });

      setSelectedOrders({});
      if (showNewOrders) loadRecentOrders();

      if (onOrdersCreated) {
        onOrdersCreated(createdOrders);
      }
    } catch (error) {
      console.error('Error signing orders:', error);
      setSnackbar({
        open: true,
        message: `Error signing orders: ${error.message}`,
        severity: 'error'
      });
    }
  };

  const totalSelected = Object.keys(selectedOrders).length;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Compact Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
          Quick Orders
        </Typography>
        {totalSelected > 0 && (
          <Chip 
            label={`${totalSelected} selected`}
            color="primary"
            size="small"
            sx={{ fontSize: '0.7rem' }}
          />
        )}
      </Box>

      {/* Grid Categories */}
      <Box 
        sx={{ 
          flexGrow: 1, 
          maxHeight,
          overflow: 'auto',
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': { background: '#f1f1f1' },
          '&::-webkit-scrollbar-thumb': { background: '#c1c1c1', borderRadius: '2px' },
        }}
      >
        <Grid container spacing={1}>
          {Object.entries(quickOrderCategories).map(([categoryKey, category]) => {
            const selectedCount = getCategorySelectedCount(categoryKey);
            const totalCount = category.items.length;
            
            return (
              <Grid item xs={4} key={categoryKey}>
                <GridCard>
                  <GridCategoryHeader 
                    title={
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {React.cloneElement(category.icon, { sx: { fontSize: '0.8rem' } })}
                          <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                            {category.title}
                          </Typography>
                        </Box>
                        {!readOnly && (
                          <Tooltip title={`Select all ${totalCount} items`}>
                            <IconButton
                              size="small"
                              onClick={() => handleSelectAllInCategory(categoryKey)}
                              sx={{ 
                                color: 'white',
                                padding: '1px',
                                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                              }}
                            >
                              <AddCircleIcon sx={{ fontSize: '0.7rem' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    }
                    sx={{ backgroundColor: category.color }}
                  />
                  <CardContent sx={{ p: 0.5, '&:last-child': { pb: 0.5 } }}>
                    {selectedCount > 0 && (
                      <Chip 
                        label={`${selectedCount}/${totalCount}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ mb: 0.5, fontSize: '0.6rem', height: '16px' }}
                      />
                    )}
                    <FormGroup>
                      {category.items.map((item) => (
                        <GridOrderItem
                          key={item.id}
                          control={
                            <Checkbox
                              checked={Boolean(selectedOrders[`${categoryKey}.${item.id}`])}
                              onChange={() => handleOrderToggle(categoryKey, item.id)}
                              size="small"
                              disabled={readOnly}
                            />
                          }
                          label={item.name}
                        />
                      ))}
                    </FormGroup>
                  </CardContent>
                </GridCard>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* Recent Orders Section - Only if enabled */}
      {showNewOrders && recentOrders.length > 0 && (
        <Box sx={{ 
          mt: 1, 
          p: 1, 
          backgroundColor: theme.palette.success.light, 
          borderRadius: 1 
        }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.success.dark }}>
            Recent Orders
          </Typography>
          {recentOrders.map((order) => (
            <Typography key={order.id} variant="caption" sx={{ display: 'block', fontSize: '0.7rem' }}>
              • {order.description || order.testType}
            </Typography>
          ))}
        </Box>
      )}

      {/* Compact Action Buttons */}
      {!readOnly && totalSelected > 0 && (
        <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
          <Button 
            variant="contained" 
            color="primary"
            size="small"
            onClick={handleSignOrders}
            sx={{ 
              fontSize: '0.75rem',
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
            }}
          >
            Sign Orders ({totalSelected})
          </Button>
          <Button 
            variant="outlined"
            size="small"
            onClick={() => setSelectedOrders({})}
            sx={{ fontSize: '0.75rem' }}
          >
            Clear
          </Button>
        </Box>
      )}

      {/* Confirmation Dialog */}
      <Dialog 
        open={confirmDialog.open} 
        onClose={() => setConfirmDialog({ open: false, orders: [] })}
        disableRestoreFocus
        aria-labelledby="confirm-order-dialog-title"
        aria-describedby="confirm-order-dialog-description"
      >
        <DialogTitle id="confirm-order-dialog-title">Confirm Order Signing</DialogTitle>
        <DialogContent id="confirm-order-dialog-description">
          <Typography variant="body2" paragraph>
            Sign {confirmDialog.orders.length} orders? These will be saved as drafts and require review in the Orders tab before sending.
          </Typography>
          {confirmDialog.orders.slice(0, 3).map((order, index) => (
            <Typography key={index} variant="body2">
              • {order.category.title}: {order.item.name}
            </Typography>
          ))}
          {confirmDialog.orders.length > 3 && (
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              ... and {confirmDialog.orders.length - 3} more
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, orders: [] })}>
            Cancel
          </Button>
          <Button onClick={confirmSignOrders} variant="contained" color="primary" autoFocus>
            Sign Orders
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default QuickListIntegrated; 